import React, {useCallback, useContext, useEffect, useState} from 'react';
import "../../assets/css/Dentobook.css";
import {
    Organization,
    Patient, ToothTreatment, Treatment,
} from "../../api-client";
import {Alert, Button, Modal, Table} from "antd";
import {FormattedMessage, useIntl} from "react-intl";
import dayjs from "dayjs";
import '../../assets/css/PatientFinances.css';
import {getPatientApi, getMedicalStaffApi, getInvoicesApi, getOrganizationApi, getTreatmentsApi} from "../../api/apiInstances";
// @ts-ignore
import {v4 as uuidv4} from 'uuid';
import {ColumnsType} from "antd/es/table";
import {calculatePayments} from "../../utils/calculationUtils";
import FormButtons from "../common/FormButtons";
import {isMobileOnly} from "react-device-detect";
import {FileAddOutlined, FileOutlined} from "@ant-design/icons";
import PatientInvoiceCreateModal from "./PatientInvoiceCreateModal";
import PatientProformaCreateModal from "./PatientProformaCreateModal";
import {TreatmentDataType} from "../../utils/patientUtils";
import {AlertContext} from "../../context/AlertContext";
import {DentobookApplicationContext} from "../../context/DentobookApplicationContext";
import {useLocation} from "react-router-dom";
import General from "../organization/General";
import logger from "../../utils/logger";
import {resolveHistoricalLabel} from "../../utils/historyUtils";
import {ALERT_DURATIONS} from "../../constants/alerts";
import {tokenService} from "../../services/tokenService";

interface PatientFinancesOperationsProps {
    patient: Patient;
}


const PatientFinancesOperations = ({patient}: PatientFinancesOperationsProps) => {

    const intl = useIntl();
    const {setError} = useContext(AlertContext);
    const location = useLocation();
    const {principal, userOrganization, locale} = useContext(DentobookApplicationContext);
    const [loading, setLoading] = useState<boolean>(false);
    const [organization, setOrganization] = useState<Organization | undefined>(undefined);
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [isProformaModalVisible, setIsProformaModalVisible] = useState<boolean>(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [treatments, setTreatments] = useState<Map<string, Treatment>>();
    const [teethTreatments, setTeethTreatments] = useState<ToothTreatment[]>([]);
    const [selectedTreatments, setSelectedTreatments] = useState<ToothTreatment[]>([]);
    const [total, setTotal] = useState<string>('00.00');

    const [createInvoiceModalOpen, setCreateInvoiceModalOpen] = useState<boolean>(false);
    const [createProformaModalOpen, setCreateProformaModalOpen] = useState<boolean>(false);

    const patientApi = getPatientApi();
    const medicalApi = getMedicalStaffApi();
    const invoicesApi = getInvoicesApi();
    const organizationApi = getOrganizationApi();
    const treatmentsApi = getTreatmentsApi();

    const rowSelection = {
        onChange: (selectedRowKeys: React.Key[], selectedRows: TreatmentDataType[]) => {
            const selectedToothTreatments = selectedRowKeys.map(key =>
                teethTreatments.find(toothTreatment => toothTreatment.id === key)
            ).filter(toothTreatment => toothTreatment !== undefined) as ToothTreatment[];

            setSelectedTreatments(selectedToothTreatments);
        }
    };

    const fetchOrganizationAndCheckConfig = useCallback(async () => {
        const orgId = tokenService.getOrganizationIdFromToken();
        if (!orgId) return;

        try {
            const response = await organizationApi.fetchOrganizationById(orgId);
            const organizationId = response.data.id;

            if (organizationId) {
                setOrganization(response.data);

            } else {
                logger.error("Organization ID is undefined");
            }
        } catch (error) {
            logger.error("Error fetching organization or invoice configuration:", error);
        }
    }, [principal?.user?.organizationId]);

    useEffect(() => {
        fetchOrganizationAndCheckConfig();
    }, [location, fetchOrganizationAndCheckConfig]);

    useEffect(() => {
        if (!patient.id) {
            return;
        }

        loadData();
    }, []);

    const loadData = () => {
        setLoading(true);

        treatmentsApi.fetchMedicalTreatments()
            .then(res => {
                const treatmentsMap = new Map();

                for (const item of res.data) {
                    treatmentsMap.set(item.id, item);
                }
                setTreatments(treatmentsMap);
                treatmentsApi.fetchMedicalTreatments()
                    .then(res => {
                        const treatmentsMap = new Map();

                        for (const item of res.data) {
                            treatmentsMap.set(item.id, item);
                        }
                        setTreatments(treatmentsMap);

                        patientApi.getPatientToothTreatments(patient.id!)
                            .then(res => {
                                setTeethTreatments(res.data);

                                const paymentsData = calculatePayments(res.data.filter(toothTreatment => toothTreatment.status === 'DONE' && !toothTreatment.invoiced));

                                setTotal(paymentsData.total.toFixed(2));
                            })
                            .catch(err => logger.log(err))
                            .finally(() => setLoading(false));
                    })
                    .catch(err => logger.log(err));
            })

        patientApi.getPatientToothTreatments(patient.id!)
            .then(res => {
                setTeethTreatments(res.data);
            })
            .catch(err => logger.log(err));
    };

    const data: TreatmentDataType[] = teethTreatments
        .filter(toothTreatment => toothTreatment.status === 'DONE' && !toothTreatment.invoiced)
        .map((toothTreatment, index) => {
            toothTreatment.procedures = toothTreatment.procedures?.map(pr => {
                return {...pr, uid: uuidv4()};
            });

            const treatmentObject = toothTreatment.treatmentId ? treatments?.get(toothTreatment.treatmentId) : undefined;

            const treatmentPrice = (toothTreatment.price ?? 0) - (toothTreatment.price ?? 0) * ((toothTreatment.discount ?? 0) / 100) +
                (toothTreatment.laboratoryPrice ?? 0);

            return {
                key: toothTreatment.id,
                date: dayjs(toothTreatment.date).format('DD.MM.YYYY'),
                procedure: <div>{resolveHistoricalLabel({ snapshot: toothTreatment.treatmentNameSnapshot, entity: treatmentObject, fallback: '' })}</div>,
                tooth: toothTreatment.tooth?.position?.toString(),
                sum: <div>{(toothTreatment.price?.toFixed(2) || '00.00') + ' ' + (userOrganization?.currency ?? 'EUR')}</div>,
                discount: <div>{(toothTreatment.discount ?? 0) + '%'}</div>,
                total: <div>{(treatmentPrice.toFixed(2) || '00.00') + ' ' + (userOrganization?.currency ?? 'EUR')}</div>,
                patientId: patient.id,
                treatment: treatmentObject
            };
        });

    const treatmentColumns: ColumnsType<TreatmentDataType> = [
        {
            title: useIntl().formatMessage({id: 'date'}),
            dataIndex: 'date',
            sorter: (dataOne, dataTwo) => {
                const tokensOne = dataOne.date!.split('.');
                const tokensTwo = dataTwo.date!.split('.');
                const firstDate = tokensOne[2] + '-' + tokensOne[1] + '-' + tokensOne[0];
                const secondDate = tokensTwo[2] + '-' + tokensTwo[1] + '-' + tokensTwo[0];

                return firstDate.localeCompare(secondDate);
            },
            key: 'date',
            width: '10%'
        },
        {
            title: useIntl().formatMessage({id: 'treatment_label'}),
            dataIndex: 'procedure',
            key: 'procedure',
            width: '40%'
        },
        {
            title: useIntl().formatMessage({id: 'tooth'}),
            dataIndex: 'tooth',
            sorter: (itemOne, itemTwo) => {
                if (+itemOne.tooth! > +itemTwo.tooth!) {
                    return 1;
                }

                return -1;
            },
            key: 'tooth',
            width: '8%'
        },
        {
            title: useIntl().formatMessage({id: 'sum'}),
            dataIndex: 'sum',
            key: 'sum',
            width: '15%'
        },
        {
            title: useIntl().formatMessage({id: 'discount_label'}),
            dataIndex: 'discount',
            key: 'discount',
            width: '10%'
        },
        {
            title: useIntl().formatMessage({id: 'total'}),
            dataIndex: 'total',
            key: 'total'
        },
    ];

    if (loading) {
        return (
            <h1><FormattedMessage id="loading"/></h1>
        );
    }

    const openProformaModal= async () => {
        if (!selectedTreatments.length) {
            setError(intl.formatMessage({id: 'select_at_least_one_item'}));
            setTimeout(() => setError(''), ALERT_DURATIONS.STANDARD);

            return;
        }
        const response = await invoicesApi.checkInvoiceExistence(organization?.id as string, "PROFORMA_INVOICE");
        if (!response.data) {
            setIsProformaModalVisible(true);
        }

        setCreateProformaModalOpen(true);
    }

    const openInvoiceModal = async () => {
        if (!selectedTreatments.length) {
            setError(intl.formatMessage({id: 'select_at_least_one_item'}));
            setTimeout(() => setError(''), ALERT_DURATIONS.STANDARD);

            return;
        }
        const response = await invoicesApi.checkInvoiceExistence(organization?.id as string, "INVOICE");

        if (!response.data) {
            setIsModalVisible(true);
        }
        
        setCreateInvoiceModalOpen(true);
    }

    const handleModalOk = () => {
        setIsModalVisible(false);
        setIsProformaModalVisible(false);
    };

    const handleModalCancel = () => {
        setIsModalVisible(false);
        setIsProformaModalVisible(false);
    };

    const showGeneralForm = () => {
        setIsEditMode(true);
        setIsModalVisible(false);
        setIsProformaModalVisible(false);
    };

    const tableFooter = () => (
        <div className="dentobook-operations-table-footer">
            <span className="font-weight-700"><FormattedMessage id="totalTwo"/>: {total} {userOrganization?.currency ?? 'EUR'}</span>
        </div>
    );

    if (isEditMode && organization) {
        return <General isEditMode={isEditMode} setIsEditMode={setIsEditMode}/>
    }

    return (
        <>
            <PatientInvoiceCreateModal isModalOpen={createInvoiceModalOpen}
                                       loadData={loadData}
                                       setIsModalOpen={setCreateInvoiceModalOpen}
                                       patient={patient}
                                       selectedTreatments={selectedTreatments}/>
            <PatientProformaCreateModal isModalOpen={createProformaModalOpen}
                                        loadData={loadData}
                                        setIsModalOpen={setCreateProformaModalOpen}
                                        patient={patient}
                                        selectedTreatments={selectedTreatments}/>
            <h3 className="font-family-roboto font-weight-500 font-24"><FormattedMessage id="done_operations"/></h3>
            <Alert
                message={<FormattedMessage id="done_operations_info"/>}
                type="info"
                showIcon
                style={{marginBottom: 16}}
            />
            <div style={{marginTop: -28}} className="dentobook-content dentobook-scrollable-table mb-32">
                <Table
                    footer={tableFooter}
                    rowSelection={{
                        type: 'checkbox',
                        ...rowSelection
                    }}
                    className={"teeth-table pt-2"}
                    columns={treatmentColumns}
                    dataSource={data}
                    pagination={false}/>
            </div>
            <FormButtons>
                <Button type="primary"
                        className="dentobook-finances-proforma-btn"
                        size={!isMobileOnly ? "large" : "middle"}
                        shape="round"
                        onClick={openProformaModal}>
                    <FileOutlined/>
                    {' '}
                    <FormattedMessage id="proforma_create"/>
                </Button>
                <Button type="primary"
                        className="dentobook-finances-invoice-btn"
                        size={!isMobileOnly ? "large" : "middle"}
                        shape="round"
                        onClick={openInvoiceModal}>
                    <FileAddOutlined/>
                    {' '}
                    <FormattedMessage id="invoice_create"/>
                </Button>
            </FormButtons>
            <Modal
                transitionName=""
                className="dentobook-modal"
                title={intl.formatMessage({id: "invoice_numbering_title"})}
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                footer={
                    <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                        <Button className="dentobook-btn-secondary modal-btn" key="close" onClick={handleModalCancel}>
                            <FormattedMessage id="close"/>
                        </Button>
                    </div>
                }
            >
                <p>
                    <FormattedMessage id="invoice_numbering_text"/>
                </p>
            </Modal>
            <Modal
                transitionName=""
                className="dentobook-modal"
                title={intl.formatMessage({id: "proforma_numbering_title"})}
                open={isProformaModalVisible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                footer={
                    <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                        <Button className="dentobook-btn-secondary modal-btn" key="close" onClick={handleModalCancel}>
                            <FormattedMessage id={"close"}/>
                        </Button>
                    </div>
                }
            >
                <p>
                    <FormattedMessage id="invoice_numbering_text"/>
                </p>
            </Modal>
        </>
    );
};

export default PatientFinancesOperations;